// Write work instructions (L3): notes, cautions, photos and checks with limits
// over the generated steps, which can be merged, split and reordered. A copy
// for a new revision starts Needs review, with what changed beside it.
import { useState } from 'react';
import { useParams } from 'react-router';

import { ApiError } from '../../api/client';
import type { CheckRequest } from '../../api/generated/CheckRequest';
import type { InstructionSetResponse } from '../../api/generated/InstructionSetResponse';
import type { InstructionStepRequest } from '../../api/generated/InstructionStepRequest';
import { useInstructionActions, useInstructionSet } from '../../api/queries';
import { canBuild, useSession } from '../../auth/session';
import { Button, ErrorPanel, Loading, PageHeader, TextField } from '../../design/components';
import { formatDateTime, titleOf } from '../../design/format';
import { draftOf, mergeWithNext, moveStep, newCheck, split, updateStep } from './editing';
import { StatusBadge } from './InstructionsPage';

export function InstructionEditorPage() {
    const { instructionSetId = '' } = useParams();
    const set = useInstructionSet(instructionSetId);
    if (set.isPending) return <Loading what="the instructions" />;
    if (set.isError) return <ErrorPanel error={set.error} title="The instructions couldn’t be loaded." />;
    return <Editor key={`${set.data.instructionSetId}-${String(set.data.version)}`} set={set.data} />;
}

function Editor({ set }: { set: InstructionSetResponse }) {
    const session = useSession();
    const canAct = canBuild(session);
    const actions = useInstructionActions(set.instructionSetId);
    // A saved edit changes the version, and the page remounts the editor from the saved set.
    const [steps, setSteps] = useState<InstructionStepRequest[]>(() => draftOf(set));
    const titles = new Map(set.structure.map((step) => [step.key, step.title]));
    const lostRace = actions.save.error instanceof ApiError && actions.save.error.code === 'Record.ChangedConcurrently';
    return (
        <section className="sf-stack" aria-labelledby="editor-heading">
            <PageHeader
                id="editor-heading"
                eyebrow="Work instructions"
                title={`${titleOf(set.assemblyPartNumber, set.assemblyName)}${set.revision ? ` · rev ${set.revision}` : ''}`}
                description={
                    <>
                        Every build of this revision follows these steps; a build that has started keeps the version it
                        started with. Last changed {formatDateTime(set.updated.at)} by {set.updated.displayName}.
                    </>
                }
                actions={<StatusBadge status={set.status} />}
            />
            {set.copiedFrom ? (
                <div className="sf-note sf-note--pending">
                    Copied from {set.copiedFrom.partNumber}
                    {set.copiedFrom.revision ? ` rev ${set.copiedFrom.revision}` : ''}. Check every step against what
                    changed:
                    {set.changes.length === 0 ? (
                        ' Engineering reported no differences.'
                    ) : (
                        <ul>
                            {set.changes.map((change) => (
                                <li key={`${change.partNumber}-${change.kind}`}>
                                    {change.partNumber}: {change.kind}
                                    {change.before !== null || change.after !== null
                                        ? ` (${String(change.before ?? '—')} → ${String(change.after ?? '—')})`
                                        : ''}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : null}
            <ol className="sf-stack" style={{ paddingLeft: 0, listStyle: 'none' }}>
                {steps.map((step, index) => (
                    <li key={step.keys.join('|')} className="sf-card sf-stack">
                        <StepEditor
                            step={step}
                            index={index}
                            count={steps.length}
                            titles={titles}
                            canAct={canAct}
                            onChange={(change) => {
                                setSteps(updateStep(steps, index, change));
                            }}
                            onMove={(by) => {
                                setSteps(moveStep(steps, index, by));
                            }}
                            onMerge={() => {
                                setSteps(mergeWithNext(steps, index));
                            }}
                            onSplit={() => {
                                setSteps(split(steps, index));
                            }}
                            onUpload={(file) => {
                                actions.uploadPhoto.mutate(file, {
                                    onSuccess: (uploaded) => {
                                        setSteps(
                                            updateStep(steps, index, { photos: [...step.photos, uploaded.photoId] }),
                                        );
                                    },
                                });
                            }}
                        />
                    </li>
                ))}
            </ol>
            {actions.uploadPhoto.isError ? (
                <ErrorPanel error={actions.uploadPhoto.error} title="The photo wasn’t uploaded." />
            ) : null}
            {lostRace ? (
                <div className="sf-note sf-note--danger" role="alert">
                    Someone else saved these instructions first. Reload the page to see their change, then make yours
                    again.
                </div>
            ) : actions.save.isError ? (
                <ErrorPanel error={actions.save.error} title="The instructions weren’t saved." />
            ) : null}
            {canAct ? (
                <div className="sf-row">
                    <Button
                        variant="primary"
                        large
                        disabled={actions.save.isPending}
                        onClick={() => {
                            actions.save.mutate({ version: set.version, steps });
                        }}
                    >
                        Save instructions
                    </Button>
                    {set.status === 'Reviewed' ? null : (
                        <Button
                            large
                            disabled={actions.review.isPending}
                            onClick={() => {
                                actions.review.mutate();
                            }}
                        >
                            Mark reviewed
                        </Button>
                    )}
                    {actions.save.isSuccess ? <span role="status">Saved.</span> : null}
                </div>
            ) : null}
        </section>
    );
}

function StepEditor({
    step,
    index,
    count,
    titles,
    canAct,
    onChange,
    onMove,
    onMerge,
    onSplit,
    onUpload,
}: {
    step: InstructionStepRequest;
    index: number;
    count: number;
    titles: ReadonlyMap<string, string>;
    canAct: boolean;
    onChange: (change: Partial<InstructionStepRequest>) => void;
    onMove: (by: -1 | 1) => void;
    onMerge: () => void;
    onSplit: () => void;
    onUpload: (file: File) => void;
}) {
    const generated = step.keys.map((key) => titles.get(key) ?? key).join(' + ');
    const setCheck = (position: number, change: Partial<CheckRequest>) => {
        onChange({ checks: step.checks.map((check, at) => (at === position ? { ...check, ...change } : check)) });
    };
    const number = (value: string) => (value.trim() === '' ? null : Number(value));
    return (
        <>
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>
                    Step {index + 1}: {step.title ?? generated}
                </h2>
                {canAct ? (
                    <div className="sf-row">
                        <Button
                            aria-label={`Move step ${String(index + 1)} up`}
                            disabled={index === 0}
                            onClick={() => {
                                onMove(-1);
                            }}
                        >
                            ↑
                        </Button>
                        <Button
                            aria-label={`Move step ${String(index + 1)} down`}
                            disabled={index === count - 1}
                            onClick={() => {
                                onMove(1);
                            }}
                        >
                            ↓
                        </Button>
                        <Button disabled={index === count - 1} onClick={onMerge}>
                            Merge with next
                        </Button>
                        <Button disabled={step.keys.length < 2} onClick={onSplit}>
                            Split
                        </Button>
                    </div>
                ) : null}
            </div>
            <p className="sf-muted">Generated: {generated}</p>
            <fieldset disabled={!canAct} className="sf-stack" style={{ border: 'none', padding: 0, margin: 0 }}>
                <TextField
                    label="Title (optional)"
                    value={step.title ?? ''}
                    onChange={(value) => {
                        onChange({ title: value || null });
                    }}
                />
                <TextField
                    label="Note"
                    value={step.note ?? ''}
                    onChange={(value) => {
                        onChange({ note: value || null });
                    }}
                    multiline
                />
                <div className="sf-stack">
                    <strong>Cautions</strong>
                    {step.cautions.map((caution, position) => (
                        <div key={position} className="sf-row">
                            <TextField
                                label={`Caution ${String(position + 1)}`}
                                value={caution}
                                onChange={(value) => {
                                    onChange({
                                        cautions: step.cautions.map((text, at) => (at === position ? value : text)),
                                    });
                                }}
                            />
                            <Button
                                onClick={() => {
                                    onChange({ cautions: step.cautions.filter((_, at) => at !== position) });
                                }}
                            >
                                Remove
                            </Button>
                        </div>
                    ))}
                    <div>
                        <Button
                            onClick={() => {
                                onChange({ cautions: [...step.cautions, ''] });
                            }}
                        >
                            Add a caution
                        </Button>
                    </div>
                </div>
                <div className="sf-stack">
                    <strong>Checks</strong>
                    {step.checks.map((check, position) => (
                        <div key={check.checkId ?? `new-${String(position)}`} className="sf-row">
                            <TextField
                                label="Check"
                                value={check.name}
                                onChange={(value) => {
                                    setCheck(position, { name: value });
                                }}
                                placeholder="Bolt torque"
                            />
                            <TextField
                                label="Unit"
                                value={check.unit ?? ''}
                                onChange={(value) => {
                                    setCheck(position, { unit: value || null });
                                }}
                                placeholder="N·m"
                            />
                            <TextField
                                label="Min"
                                inputMode="decimal"
                                value={check.min === null ? '' : String(check.min)}
                                onChange={(value) => {
                                    setCheck(position, { min: number(value) });
                                }}
                            />
                            <TextField
                                label="Max"
                                inputMode="decimal"
                                value={check.max === null ? '' : String(check.max)}
                                onChange={(value) => {
                                    setCheck(position, { max: number(value) });
                                }}
                            />
                            <Button
                                onClick={() => {
                                    onChange({ checks: step.checks.filter((_, at) => at !== position) });
                                }}
                            >
                                Remove
                            </Button>
                        </div>
                    ))}
                    <div>
                        <Button
                            onClick={() => {
                                onChange({ checks: [...step.checks, newCheck()] });
                            }}
                        >
                            Add a check
                        </Button>
                    </div>
                </div>
                <div className="sf-stack">
                    <strong>Photos</strong>
                    <div className="sf-photo-strip">
                        {step.photos.map((photo, position) => (
                            <figure key={photo} style={{ margin: 0 }}>
                                <img
                                    src={`/api/v1/instruction-photos/${photo}`}
                                    alt={`Step ${String(index + 1)}, view ${String(position + 1)}`}
                                />
                                <figcaption>
                                    <Button
                                        onClick={() => {
                                            onChange({ photos: step.photos.filter((id) => id !== photo) });
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                    <div className="sf-field">
                        <label htmlFor={`photo-${step.keys.join('-')}`}>Add a photo</label>
                        <input
                            id={`photo-${step.keys.join('-')}`}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) onUpload(file);
                            }}
                        />
                    </div>
                </div>
            </fieldset>
        </>
    );
}
